import type { CSSProperties } from 'react'
import { branding } from '@/shared/config/branding'
import { useTheme } from '@/shared/lib/useTheme'
import { cn } from '@/shared/lib/cn'

interface SynapBrandLogoProps {
  className?: string
  /**
   * `sidebar`/`on-light`: squircle branco fixo + asset pré-composto (mesmo em ambos os temas).
   * `on-dark` (splash): traço branco sobre gradiente escuro, sem container.
   * `adaptive`: traço sem fundo resolvido por tema (legado).
   */
  variant?: 'adaptive' | 'on-dark' | 'on-light' | 'sidebar'
}

/** Bust de cache para SVG em /public (PWA pode servir asset antigo pré-outline). */
const withAppVersion = (path: string): string => {
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}v=${encodeURIComponent(__APP_VERSION__)}`
}

/** Raio squircle iOS-like — alinhado a generate-icons.mjs (SQUIRCLE_RADIUS_RATIO). */
const SQUIRCLE_RADIUS = '22.37%'

/** Container claro fixo: não inverte com o tema (docs/internal/logo-background-proposal.html). */
const chipStyle: CSSProperties = {
  borderRadius: SQUIRCLE_RADIUS,
  backgroundColor: 'var(--logo-chip-bg)',
  border: '1px solid var(--logo-chip-border)',
}

export const SynapBrandLogo = ({ className, variant = 'adaptive' }: SynapBrandLogoProps) => {
  const { resolved } = useTheme()

  if (variant === 'sidebar' || variant === 'on-light') {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 items-center justify-center',
          variant === 'on-light' ? 'p-2' : 'p-1',
          className,
        )}
        style={chipStyle}
      >
        <img
          src={withAppVersion(branding.iconAppLightPath)}
          alt=""
          aria-hidden
          className="block h-full w-full object-contain"
        />
      </span>
    )
  }

  /** Traço teal (tema claro) vs mono branco (fundo escuro). */
  const useTealStroke = variant === 'on-dark' ? false : resolved !== 'dark'
  const src = withAppVersion(
    useTealStroke ? branding.iconTransparentPath : branding.iconTransparentDarkPath,
  )

  const onDarkStyle: CSSProperties | undefined =
    variant === 'on-dark'
      ? {
          objectFit: 'contain',
          maxWidth: '100%',
        }
      : undefined

  return (
    <img
      src={src}
      alt=""
      aria-hidden
      className={cn('shrink-0 object-contain', className)}
      style={onDarkStyle}
    />
  )
}
